import * as Yup from 'yup';

import { TIME_FORMAT } from '@/components/ui/forms/TimeInputs';

import { contactTypeOptions, contactTypeReasonsV2 } from '../constants';
import { type ContactType, type FormValues, type SubmitValues } from '../types';

export const validationSchema = () => {
  const detailCommentMaxLength = 400;
  const diseaseNameMaxLength = 30;
  const symptomNameMaxLength = 30;
  const transportationMaxLength = 30;
  const relationshipMaxLength = 30;
  const bodyTemperatureDigitNumber = /^[3-4]\d(\.\d)?$/;

  return Yup.object().shape({
    absence: Yup.object().shape({
      date: Yup.string().required('日付は必須です'),
    }),
    contact_type: Yup.string()
      .required('内容は必須です')
      .oneOf(
        contactTypeOptions.map((opt) => opt.value).concat(['']),
        '正しい値で入力してください',
      ),
    reason: Yup.string()
      .required('理由は必須です')
      .when('contact_type', ([val]: string[], schema: Yup.StringSchema) =>
        val
          ? schema.oneOf(
              contactTypeReasonsV2[val as ContactType].concat(['']),
              '正しい値で入力してください',
            )
          : schema,
      ),
    go_to_school_at: Yup.string().when(
      'contact_type',
      ([val]: string[], schema: Yup.StringSchema) =>
        val === 'tardy'
          ? schema.required('登校時刻は必須です').matches(TIME_FORMAT, {
              excludeEmptyString: true,
              message: '正しい値で入力してください',
            })
          : schema,
    ),
    leave_school_at: Yup.string().when(
      'contact_type',
      ([val]: string[], schema: Yup.StringSchema) =>
        val === 'early_leaving'
          ? schema.required('下校時刻は必須です').matches(TIME_FORMAT, {
              excludeEmptyString: true,
              message: '正しい値で入力してください',
            })
          : schema,
    ),
    disease_name: Yup.string().when(
      'reason',
      ([val]: string[], schema: Yup.StringSchema) =>
        ['poor_health', 'injury', 'visit_hospital'].includes(val)
          ? schema
              .trim()
              .max(
                diseaseNameMaxLength,
                `病名は${diseaseNameMaxLength}文字以内で入力してください`,
              )
          : schema,
    ),
    symptom: Yup.string().when(
      'reason',
      ([val]: string[], schema: Yup.StringSchema) =>
        ['poor_health', 'injury', 'visit_hospital'].includes(val)
          ? schema
              .trim()
              .required('症状は必須です')
              .max(
                symptomNameMaxLength,
                `症状は${symptomNameMaxLength}文字以内で入力してください`,
              )
          : schema,
    ),
    body_temperature: Yup.string().when(
      'reason',
      ([val]: string[], schema: Yup.StringSchema) =>
        ['poor_health', 'injury', 'visit_hospital'].includes(val)
          ? schema.trim().matches(bodyTemperatureDigitNumber, {
              excludeEmptyString: true,
              message: '半角数字で30.0~49.9の範囲内で入力してください',
            })
          : schema,
    ),
    doctors_instruction: Yup.string().when(
      'reason',
      ([val]: string[], schema: Yup.StringSchema) =>
        ['poor_health', 'injury', 'visit_hospital'].includes(val)
          ? schema.oneOf(['1', '0', ''], '正しい値で入力してください')
          : schema,
    ),
    relationship: Yup.string().when(
      'reason',
      ([val]: string[], schema: Yup.StringSchema) =>
        val === 'mourning'
          ? schema
              .trim()
              .required('亡くなった方と生徒の関係は必須です')
              .max(
                relationshipMaxLength,
                `亡くなった方と生徒の関係は${relationshipMaxLength}文字以内で入力してください`,
              )
          : schema,
    ),
    transportation: Yup.string().when(
      'reason',
      ([val]: string[], schema: Yup.StringSchema) => {
        if (['traffic_delay', 'traffic_stop'].includes(val)) {
          return schema
            .trim()
            .required('交通機関は必須です')
            .max(
              transportationMaxLength,
              `交通機関は${transportationMaxLength}文字以内で入力してください`,
            );
        } else {
          return schema;
        }
      },
    ),
    detail_comment: Yup.string().when(
      'reason',
      ([val]: string[], schema: Yup.StringSchema) =>
        val === 'family' || val === 'others'
          ? schema
              .trim()
              .required('詳細コメントは必須です')
              .max(
                detailCommentMaxLength,
                `詳細コメントは${detailCommentMaxLength}文字以内で入力してください`,
              )
          : schema
              .trim()
              .max(
                detailCommentMaxLength,
                `詳細コメントは${detailCommentMaxLength}文字以内で入力してください`,
              ),
    ),
  });
};

// 'absence.date'がRHF内で整形されvaluesとしては{absence: {date: string}}で返されるのでtypeを無理やり変更
export const convertToSubmitValuesWithOmId = (
  values: FormValues,
  organizationMembershipId: string,
): SubmitValues => {
  const { 'absence.date': _, ...submitValues } = values;
  (submitValues as SubmitValues).absence.organization_membership_id =
    organizationMembershipId;

  return submitValues as SubmitValues;
};
